import { useEffect, useState } from "react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Loader2, DollarSign, ShoppingBag, Mail, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/book";
import AdminLayout from "@/pages/admin/AdminLayout";

const apiGet = (path) => axios.get(`${API}${path}`, { withCredentials: true }).then((r) => r.data);

function Stat({ label, value, sub, Icon }) {
  return (
    <div className="bg-white border border-black/10 p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-[#D92D20]" />}
      </div>
      <div className="font-display font-black tracking-tighter text-4xl leading-none">{value}</div>
      {sub && <div className="mt-2 text-xs text-[#525252]">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [chart, setChart] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const fetchAll = async () => {
    const [s, c, o] = await Promise.all([
      apiGet("/admin/stats"),
      apiGet("/admin/sales-chart?days=30"),
      apiGet("/admin/orders?limit=10"),
    ]);
    return { stats: s, chart: c, orders: o.orders || [] };
  };

  useEffect(() => {
    let active = true;
    fetchAll()
      .then(({ stats: s, chart: c, orders: o }) => {
        if (!active) return;
        setStats(s);
        setChart(c);
        setOrders(o);
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const handleResetTestData = async () => {
    const confirmed = window.confirm(
      "Smart cleanup: removes abandoned/incomplete payments and obvious test leads/messages (e.g. @example.com).\n\nReal orders are NEVER deleted.\n\nContinue?"
    );
    if (!confirmed) return;
    setResetting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/reset-test-data`,
        {},
        { withCredentials: true }
      );
      const d = data.deleted || {};
      toast.success(
        `Cleared ${d.payment_transactions} abandoned txns · ${d.leads} test leads · ${d.contact_messages} test messages`
      );
      setLoading(true);
      const next = await fetchAll();
      setStats(next.stats);
      setChart(next.chart);
      setOrders(next.orders);
      setLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-3">Overview</div>
          <h1 className="font-display font-black tracking-tighter text-5xl leading-[0.95]" data-testid="admin-dashboard-title">
            Dashboard
          </h1>
        </div>
        <button
          onClick={handleResetTestData}
          disabled={resetting || loading}
          className="inline-flex items-center gap-2 border border-black/15 px-4 py-2.5 text-[10px] uppercase tracking-[0.25em] font-bold hover:border-[#D92D20] hover:text-[#D92D20] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="admin-reset-test-data"
        >
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          Clean test data
        </button>
      </div>

      {loading || !stats ? (
        <div className="flex items-center gap-3 text-sm text-[#525252]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-stat-grid">
            <Stat label="Revenue · Today" value={`$${stats.today.revenue.toFixed(2)}`} sub={`${stats.today.orders} orders`} Icon={DollarSign} />
            <Stat label="Revenue · 7 days" value={`$${stats.week.revenue.toFixed(2)}`} sub={`${stats.week.orders} orders`} Icon={DollarSign} />
            <Stat label="Revenue · 30 days" value={`$${stats.month.revenue.toFixed(2)}`} sub={`${stats.month.orders} orders`} Icon={DollarSign} />
            <Stat label="Revenue · All time" value={`$${stats.all.revenue.toFixed(2)}`} sub={`${stats.all.orders} orders · ${stats.all.units} units`} Icon={DollarSign} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
            <Stat label="Email list" value={stats.leads_total} sub="Newsletter subscribers" Icon={Mail} />
            <Stat label="Pending payments" value={stats.pending_transactions} sub="Abandoned or in-progress" Icon={ShoppingBag} />
            <Stat label="Support messages" value={stats.contact_messages_total} sub="All-time inquiries" Icon={Tag} />
          </div>

          {/* Sales chart */}
          <div className="mt-10 bg-white border border-black/10 p-6" data-testid="admin-sales-chart">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold">Revenue · last 30 days</div>
                <div className="font-display font-black tracking-tighter text-3xl mt-1">${chart?.total_revenue?.toFixed(2)}</div>
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={chart?.series || []} margin={{ top: 5, right: 16, left: -16, bottom: 0 }}>
                  <CartesianGrid stroke="#EEE" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", fontSize: 12 }} formatter={(v, k) => k === "revenue" ? [`$${Number(v).toFixed(2)}`, "Revenue"] : [v, k]} />
                  <Line type="monotone" dataKey="revenue" stroke="#D92D20" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent orders */}
          <div className="mt-10">
            <div className="flex items-end justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold">Latest orders</div>
              <a href="/admin/orders" className="text-xs underline">View all</a>
            </div>
            <div className="bg-white border border-black/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F5]">
                  <tr>
                    <Th>Date</Th><Th>Customer</Th><Th>Qty</Th><Th>Total</Th><Th>Ship to</Th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-[#525252] text-sm">No orders yet.</td></tr>
                  ) : orders.map((o) => (
                    <tr key={o.id} className="border-t border-black/10" data-testid={`admin-order-row-${o.id}`}>
                      <Td>{(o.created_at || "").slice(0, 10)}</Td>
                      <Td>
                        <div className="font-medium">{o.customer_name}</div>
                        <div className="text-xs text-[#525252]">{o.customer_email}</div>
                      </Td>
                      <Td>{o.quantity}</Td>
                      <Td className="font-mono-plex">${o.amount_total?.toFixed(2)}</Td>
                      <Td className="text-xs">{o.shipping_address?.city}, {o.shipping_address?.country}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

const Th = ({ children }) => <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-5 py-4 ${className}`}>{children}</td>;
